/**
 * 69.30 (ARCHITECTURE §5.26, DECISIONS 248): the ammunition by its type, as the dumps hold it — the owner: *"eventually we'll
 * want individual ammo, but can be in boxes with multiple types maybe"*. A crate per type in the supplies' language (67.75:
 * rendering/supplyLoads.ts, the ships' kit — merged, the paint per vertex, one draw a crate), its band the type's code; open,
 * its rounds inside as the game draws them (the LAW, the anti-dropship missile, the mortar bomb, the tank's two rounds, the
 * light shell); the boxes mixed (one crate with a vehicle's load); 69.34, a dump's rounds drawn in play: typed crates in
 * mixed stacks that shrink as its counts fall (`buildAmmoDump`). Presentation only (design_sheets/munitions.html); the sim's
 * counts are lane F's (core/munitions.ts).
 *
 * The types the sim's (types AmmoType): the rocket, the anti-dropship missile, the mortar bomb, the tank's explosive and
 * solid rounds, the light shell. Metres (a person is 1.8), a crate's origin the middle of its base.
 */
import * as THREE from 'three';
import { Spec, mergedTier, type Livery, type Paint } from './orbitShips';
import { PALLET } from '../constants/logistics';
import type { UnitPalette } from './unitPalette';
import type { AmmoType } from '../types';
import { MUNITIONS } from '../constants';
import { TECHNICAL_MODEL, SUPPLY_TRUCK_MODEL, type VehicleModel } from './vehicleModels';
import { buildLaw, setLawExtended } from './groundWeapons';
import { buildAntiDropshipMissile, setAntiDropshipMissileRaised } from './antiDropshipMissileLooks';
import { buildTankRound } from './tankLooks';
import { buildMortarBomb } from './mortarLooks';

export type { AmmoType };
export const AMMO_TYPES: readonly AmmoType[] = ['rocket', 'missile', 'mortarBomb', 'tankHe', 'tankSolid', 'lightShell'];
/**
 * Each type's crate: its name, rounds a crate, its size (length along x, width, height), its wood or metal, its band (the
 * type's code, read from the diorama: the rocket's red, the missile's yellow on slate, the bomb's yellow, the explosive
 * round's yellow, the solid round's black, the shell's red on wood), and how its rounds lie in it.
 */
export const AMMO_CRATES: Record<AmmoType, { name: string; per: number; l: number; w: number; h: number; body: Paint; band: Paint }> = {
  rocket: { name: 'rocket (LAW)', per: 2, l: 1.12, w: 0.4, h: 0.26, body: 'olive', band: 'hazard' },
  missile: { name: 'anti-dropship missile', per: 1, l: 1.62, w: 0.3, h: 0.4, body: 'slate', band: 'caution' },
  mortarBomb: { name: 'mortar bomb', per: 3, l: 0.74, w: 0.42, h: 0.17, body: 'tan', band: 'caution' },
  tankHe: { name: 'tank, explosive', per: 2, l: 1.08, w: 0.34, h: 0.19, body: 'olive', band: 'caution' },
  tankSolid: { name: 'tank, solid (the dart)', per: 2, l: 1.2, w: 0.34, h: 0.19, body: 'olive', band: 'machDk' },
  lightShell: { name: 'light shell', per: 2, l: 0.92, w: 0.34, h: 0.2, body: 'tan', band: 'hazard' },
};

const LIVERY: Livery = { name: '', mark: 0 };
let crateMat: THREE.MeshLambertMaterial | null = null;
const CRATE_MAT = () => (crateMat ??= new THREE.MeshLambertMaterial({ vertexColors: true }));
function looked(spec: Spec, p: UnitPalette): THREE.Mesh {
  const g = mergedTier(spec, p, false);
  g.setAttribute('color', g.getAttribute('paint'));
  g.deleteAttribute('glow');
  return new THREE.Mesh(g, CRATE_MAT());
}

/** A crate's spec at (x, y, z): the body (or, open, its floor and four walls), its lid's seam, the band, the stencil plate, the end handles. */
function crateSpec(s: Spec, type: AmmoType, open: boolean, x = 0, y = 0, z = 0): void {
  const c = AMMO_CRATES[type], { l, w, h } = c;
  if (!open) {
    s.box(l, h, w, x, y + h / 2, z, c.body);
    s.box(l + 0.006, 0.012, w + 0.006, x, y + h - 0.03, z, 'machDk');   // the lid's seam
  } else {
    const t = 0.02;
    s.box(l, t, w, x, y + t / 2, z, c.body);
    for (const sz of [-1, 1]) s.box(l, h - 0.03, t, x, y + (h - 0.03) / 2, z + sz * (w / 2 - t / 2), c.body);
    for (const sx of [-1, 1]) s.box(t, h - 0.03, w, x + sx * (l / 2 - t / 2), y + (h - 0.03) / 2, z, c.body);
  }
  // the band round it near one end, the stencil plate on its side, a handle at each end
  s.box(0.06, h + 0.004, w + 0.004, x + l / 2 - 0.14, y + h / 2, z, c.band);
  s.box(0.26, 0.07, 0.004, x - l / 6, y + h * 0.55, z + w / 2 + 0.002, 'cream');
  for (const sx of [-1, 1]) s.box(0.02, 0.03, 0.12, x + sx * (l / 2 + 0.01), y + h * 0.6, z, 'metal');
}

/** A crate of `type`, closed: one mesh, one draw. */
export function buildAmmoCrate(type: AmmoType, p: UnitPalette): THREE.Mesh {
  const s = new Spec(LIVERY);
  crateSpec(s, type, false);
  return looked(s, p);
}

/** One round of `type` as the game draws it, lying along +x with its middle at the origin. */
export function buildAmmoRound(type: AmmoType, p: UnitPalette): THREE.Object3D {
  const g = new THREE.Group();
  if (type === 'rocket') { const law = buildLaw(p); setLawExtended(law, 0); law.group.rotation.y = Math.PI / 2; g.add(law.group); }
  else if (type === 'missile') { const m = buildAntiDropshipMissile(p, undefined, { bake: false }); setAntiDropshipMissileRaised(m, 0); m.group.rotation.y = Math.PI / 2; m.group.position.x = 0.12; g.add(m.group); }
  else if (type === 'mortarBomb') { const b = buildMortarBomb('A'); b.rotation.z = -Math.PI / 2; b.position.x = -0.31; g.add(b); }
  else if (type === 'tankHe' || type === 'tankSolid') { const r = buildTankRound(type === 'tankHe' ? 'he' : 'solid', 'A', 'cartridge'); r.rotation.y = Math.PI / 2; r.position.x = -0.5; g.add(r); }
  else { const r = buildTankRound('he', 'B', 'cartridge'); r.scale.setScalar(0.75); r.rotation.y = Math.PI / 2; r.position.x = -0.42; g.add(r); }
  // centred along its length and across, resting on y 0 (each drawing's own origin differs: the tube's grip, a case's base)
  g.updateMatrixWorld(true);
  const b = new THREE.Box3().setFromObject(g), c = b.getCenter(new THREE.Vector3());
  for (const k of g.children) k.position.add(new THREE.Vector3(-c.x, -b.min.y, -c.z));
  return g;
}

/** A crate of `type`, open: the crate (one draw), its lid leaning against it, its rounds in it as the game draws them. */
export function buildOpenAmmoCrate(type: AmmoType, p: UnitPalette): THREE.Group {
  const c = AMMO_CRATES[type], g = new THREE.Group();
  const s = new Spec(LIVERY);
  crateSpec(s, type, true);
  g.add(looked(s, p));
  const lid = new Spec(LIVERY); lid.box(c.l, 0.02, c.w, 0, 0, 0, c.body); lid.box(0.06, 0.024, c.w + 0.004, c.l / 2 - 0.14, 0, 0, c.band);
  const lidMesh = looked(lid, p); lidMesh.position.set(0, c.h * 0.6, -c.w / 2 - 0.08); lidMesh.rotation.x = 1.2; g.add(lidMesh);
  const across = c.per <= 2 ? c.per : c.per === 3 ? 3 : 2, rows = Math.ceil(c.per / across);
  for (let i = 0; i < c.per; i++) {
    const r = buildAmmoRound(type, p), col = i % across, row = Math.floor(i / across);
    r.position.set(0, 0.02 + row * 0.13 * (rows > 1 ? 1 : 0), -c.w / 2 + (col + 0.5) * (c.w / across));
    g.add(r);
  }
  return g;
}

/**
 * A mixed box (option B): one crate holding a vehicle's mixed load — `load` the rounds by type — its compartments by type, its
 * bands the types', open: its rounds inside. One crate a vehicle's rearm (the tank's: 2 explosive, 1 solid; the launchers': 2
 * LAWs, 1 missile).
 */
export function buildMixedCrate(load: Partial<Record<AmmoType, number>>, p: UnitPalette, open = true): THREE.Group {
  const g = new THREE.Group(), types = AMMO_TYPES.filter(t => (load[t] ?? 0) > 0);
  const rounds = types.flatMap(t => Array.from({ length: load[t]! }, () => t));
  const l = Math.max(...types.map(t => AMMO_CRATES[t].l)) + 0.06, w = 0.12 * rounds.length + 0.12, h = 0.24;
  const s = new Spec(LIVERY);
  if (open) {
    s.box(l, 0.02, w, 0, 0.01, 0, 'machLt');
    for (const sz of [-1, 1]) s.box(l, h, 0.02, 0, h / 2, sz * (w / 2 - 0.01), 'machLt');
    for (const sx of [-1, 1]) s.box(0.02, h, w, sx * (l / 2 - 0.01), h / 2, 0, 'machLt');
  } else s.box(l, h, w, 0, h / 2, 0, 'machLt');
  // a band for each type it holds, side by side at one end; the compartments' walls between the types
  types.forEach((t, k) => s.box(0.05, h + 0.004, w + 0.004, l / 2 - 0.1 - k * 0.07, h / 2, 0, AMMO_CRATES[t].band));
  s.box(0.3, 0.08, 0.004, -l / 6, h * 0.55, w / 2 + 0.002, 'cream');
  let z = -w / 2 + 0.06, last: AmmoType | null = null;
  for (const t of rounds) {
    if (open && last && t !== last) s.box(l - 0.04, h - 0.04, 0.012, 0, (h - 0.04) / 2, z, 'machDk');   // a compartment's wall
    if (open) { const r = buildAmmoRound(t, p); r.position.set(0, 0.02, z + 0.06); g.add(r); }
    z += 0.12; last = t;
  }
  g.add(looked(s, p));
  return g;
}

/**
 * 69.34 (DECISIONS 251, the owner: *"ammo individually -> stored in like crates -> these crates can be combined in stacks where
 * you have some crates of different ammo types stacked on top of eachother -> these stacks should be combineable into the
 * shipping container"*; *"Stacks shrink. Colored bands per type"*): a dump's rounds as crates of one type (`cratesOf`: a crate
 * per AMMO_CRATES[t].per rounds, the last part full), stacked with types mixed on pallets (`stackCrates`) — each stack a
 * pallet's footprint (two end to end for the missile's long case) and no taller than a pallet load (PALLET.height), so the
 * container's packing (69.10, 67.75's CONTAINER_SPOTS) can take a stack as a pallet later. Pure: the same counts, the same stacks.
 */
export function cratesOf(rounds: Partial<Record<AmmoType, number>> | undefined): Partial<Record<AmmoType, number>> {
  const out: Partial<Record<AmmoType, number>> = {};
  for (const t of AMMO_TYPES) { const n = Math.ceil(Math.max(0, rounds?.[t] ?? 0) / AMMO_CRATES[t].per); if (n) out[t] = n; }
  return out;
}
/** 69.34: the order crates stack in — the missile's long case first (it opens the two-spot stack), the heavy rounds low. */
export const AMMO_STACK_ORDER: readonly AmmoType[] = ['missile', 'tankSolid', 'tankHe', 'rocket', 'mortarBomb', 'lightShell'];
/** A crate in its stack: its type and the middle of its base, in the stack's frame (the pallet's base the origin, its length along x). */
export interface StackedCrate { type: AmmoType; x: number; y: number; z: number }
/** A stack: on one pallet spot or two end to end, its crates, its top. */
export interface AmmoStack { spots: 1 | 2; crates: StackedCrate[]; top: number }
/** The pallet's deck (its runners and boards); a crate's gap to its neighbour. */
const DECK = 0.13, GAP = 0.015;
/** Where a stack stands: its footprint (length along x, width), the height it keeps under, the floor its crates start on, and whether a crate longer than it takes two spots. */
export interface StackRoom { length: number; width: number; height: number; deck: number; twoSpots: boolean }
/** 69.34: a pallet load — the dumps' stacks, and the container's packing's later (PALLET, CONTAINER_SPOTS). */
export const PALLET_ROOM: StackRoom = { length: PALLET.length, width: PALLET.width, height: PALLET.height, deck: DECK, twoSpots: true };

/**
 * 69.34: `crates` (a count a type) stacked: in AMMO_STACK_ORDER, a type's crates in rows across the pallet (as many end to end
 * as its length takes), the next type's rows beside them, a layer up when the width is full — each crate resting on the
 * highest crate under it (types of different heights mix) — and a new stack when a crate would stand over PALLET.height (or is
 * longer than the pallet: two spots). The last stack the short one: as a dump's counts fall, its stacks shrink from the last.
 */
export function stackCrates(crates: Partial<Record<AmmoType, number>>, room: StackRoom = PALLET_ROOM): AmmoStack[] {
  const out: AmmoStack[] = [], W = room.width;
  let st: AmmoStack | null = null, L = 0, z0 = -W / 2, row: AmmoType | null = null, rowW = 0, col = 0;
  const open = (l: number): AmmoStack => {
    const s: AmmoStack = { spots: room.twoSpots && l > room.length + 0.02 ? 2 : 1, crates: [], top: room.deck };
    out.push(s); st = s;
    L = room.length * s.spots; z0 = -W / 2; row = null; rowW = 0; col = 0;
    return s;
  };
  for (const t of AMMO_STACK_ORDER) {
    const c = AMMO_CRATES[t];
    for (let i = 0; i < (crates[t] ?? 0); i++) {
      let s: AmmoStack = st && c.l <= L + 0.02 ? st : open(c.l);
      for (let tries = 0; ; tries++) {
        const across = Math.max(1, Math.floor((L + 0.02) / c.l));
        if (row !== t || col >= across) {   // a new row: beside the last, else a layer up (from the pallet's near side)
          if (row !== null) z0 += rowW + GAP;
          if (z0 + c.w > W / 2 + 0.02) z0 = -W / 2;
          row = t; rowW = c.w; col = 0;
        }
        const x = -L / 2 + (col + 0.5) * (L / across), z = z0 + c.w / 2, y = restOn(s.crates, t, x, z, room.deck);
        if (y + c.h <= room.height || tries > 0) {   // (a fresh stack always takes it)
          s.crates.push({ type: t, x, y, z });
          s.top = Math.max(s.top, y + c.h); col++;
          break;
        }
        s = open(c.l);   // too tall: a new stack
      }
    }
  }
  return out;
}

/** Where a crate of `t` at (x, z) rests among `under`: the deck, or the top of the highest crate under its footprint. */
function restOn(under: readonly StackedCrate[], t: AmmoType, x: number, z: number, deck: number): number {
  const c = AMMO_CRATES[t];
  let y = deck;
  for (const o of under) {
    const k = AMMO_CRATES[o.type];
    if (Math.abs(o.x - x) < (k.l + c.l) / 2 - 1e-6 && Math.abs(o.z - z) < (k.w + c.w) / 2 - 1e-6) y = Math.max(y, o.y + k.h + 0.01);
  }
  return y;
}

/**
 * 69.34: a dump's stacks as `crates` shows them against its plan (`stackCrates` of its fullest counts): of each type, its
 * last-stacked crates gone first (the top of the last stack holding it), every crate left settling onto what is still under
 * it. No crate moves across the dump and no stack grows — the stacks only shrink (laid out afresh from the counts, a round's
 * worth taken would shuffle the crates after it, and now and then take a stack more). A stack left empty keeps its place.
 */
export function shownStacks(plan: readonly AmmoStack[], crates: Partial<Record<AmmoType, number>>, deck = DECK): AmmoStack[] {
  const seen = new Map<AmmoType, number>();
  return plan.map(s => {
    const kept = s.crates.filter(c => { const k = seen.get(c.type) ?? 0; seen.set(c.type, k + 1); return k < (crates[c.type] ?? 0); });
    const settled: StackedCrate[] = [];
    let top = deck;
    for (const c of [...kept].sort((a, b) => a.y - b.y)) {
      const y = restOn(settled, c.type, c.x, c.z, deck);
      settled.push({ ...c, y }); top = Math.max(top, y + AMMO_CRATES[c.type].h);
    }
    return { spots: s.spots, crates: settled, top };
  });
}

/** 69.34: where a dump's stacks stand — `perRow` side by side (across their width, z), the rows one behind the other (x); the dump's origin the first row's near corner, its extent. */
export function layDump(stacks: readonly AmmoStack[], perRow: number, room: StackRoom = PALLET_ROOM): { at: Array<{ x: number; z: number }>; length: number; width: number } {
  const at: Array<{ x: number; z: number }> = [], pitch = room.width + 0.2;
  let x = 0, rowLen = 0, width = 0;
  stacks.forEach((s, i) => {
    const k = i % perRow;
    if (k === 0 && i > 0) { x += rowLen + 0.3; rowLen = 0; }
    const l = room.length * s.spots;
    rowLen = Math.max(rowLen, l);
    at.push({ x: x + l / 2, z: k * pitch + room.width / 2 });
    width = Math.max(width, k * pitch + room.width);
  });
  return { at, length: x + rowLen, width };
}

/**
 * 69.34: a dump drawn from its rounds — each type's crates one instanced mesh (its band the type's colour), the pallets one
 * per size; `showAmmoDump` lays it out again when the counts change (a rearm takes a round every second or so: a few dozen
 * matrices then, nothing between). Its origin the dump's near corner on the ground, metres (a person 1.8).
 */
export interface AmmoDump {
  group: THREE.Group;
  crates: Partial<Record<AmmoType, THREE.InstancedMesh>>;
  pallets: Partial<Record<1 | 2, THREE.InstancedMesh>>;
  perRow: number;
  /** where its stacks stand (pallets when it has a deck), and the share of its rounds it draws (a truck's bed: what fits) */
  room: StackRoom;
  share: number;
  /** the counts last shown (a key); the plan, laid out at the dump's fullest (crates a type), and the extent it takes */
  shown: string;
  plan: { full: Partial<Record<AmmoType, number>>; stacks: AmmoStack[]; at: Array<{ x: number; z: number }> } | null;
  extent: { length: number; width: number };
  p: UnitPalette;
  /** 69.31: the stacks as last shown (`shownStacks`), where the dump's arm picks its crates (`topCrateAt`) */
  stacks?: AmmoStack[];
}
export function buildAmmoDump(p: UnitPalette, perRow = 4, room: StackRoom = PALLET_ROOM, share = 1): AmmoDump {
  const group = new THREE.Group(); group.name = 'ammo dump';
  return { group, crates: {}, pallets: {}, perRow, room, share, shown: '', plan: null, extent: { length: 0, width: 0 }, p };
}

/**
 * 69.34: the supply truck's bed as a dump (MUNITIONS.truck, its rounds `StructureState.ammo`): the tailgate's row of the bed
 * (the front two rows its cargo's, under the tarp), the crates on its floor across it, no pallet. The bed holds a share of a
 * full load's crates (`bedShare`, about half), so it draws that share of its rounds — shrinking with them all the way down.
 * Metres (a person 1.8), its origin the row's near corner on the bed's floor.
 */
export const BED_ROOM: StackRoom = { length: 1.58, width: 0.74, height: 0.86, deck: 0, twoSpots: false };
/** The largest share (in twentieths) of `full`'s rounds whose crates stack in one bed. */
export function bedShare(full: Partial<Record<AmmoType, number>>): number {
  for (let k = 20; k > 1; k--) {
    const part = Object.fromEntries(AMMO_TYPES.map(t => [t, Math.ceil((full[t] ?? 0) * k / 20)]));
    if (stackCrates(cratesOf(part), BED_ROOM).length <= 1) return k / 20;
  }
  return 0.05;
}
export function buildAmmoBed(p: UnitPalette, full: Partial<Record<AmmoType, number>>): AmmoDump {
  const d = buildAmmoDump(p, 1, BED_ROOM, bedShare(full));
  d.group.name = 'ammo bed';
  return d;
}
/** The pallet: three runners, the deck boards (supplyLoads' pallet), one spot or two end to end. */
function palletGeometry(spots: 1 | 2, p: UnitPalette): THREE.BufferGeometry {
  const ps = new Spec(LIVERY), L = PALLET.length * spots, W = PALLET.width;
  for (const z of [-1, 0, 1]) ps.box(L, 0.09, 0.1, 0, 0.045, z * (W / 2 - 0.05), 'tan');
  for (let k = 0; k < 7 * spots; k++) ps.box(0.12, 0.03, W, -L / 2 + 0.06 + (k * (L - 0.12)) / (7 * spots - 1), 0.115, 0, 'tan');
  return looked(ps, p).geometry;
}
/** An instanced layer with room for `n`, made again larger when it has too little. */
function room(d: AmmoDump, have: THREE.InstancedMesh | undefined, n: number, make: () => THREE.BufferGeometry, name: string): THREE.InstancedMesh | undefined {
  if (!n && !have) return undefined;
  if (have && have.instanceMatrix.count >= n) return have;
  const geometry = have?.geometry ?? make();
  if (have) { d.group.remove(have); have.dispose(); }
  const im = new THREE.InstancedMesh(geometry, CRATE_MAT(), Math.max(n, 4));
  im.name = name;
  d.group.add(im);
  return im;
}
const _m = new THREE.Matrix4();
/**
 * 69.34: the dump showing `rounds` (by type): its crates, stacked — against its plan (`shownStacks`), laid out afresh only when a
 * type holds more crates than the plan has (the first sight, a dump topped up past it). Nothing done when a crate's worth has
 * not moved. Returns whether it changed.
 */
export function showAmmoDump(d: AmmoDump, rounds: Partial<Record<AmmoType, number>> | undefined): boolean {
  const scaled = d.share >= 1 || !rounds ? rounds : Object.fromEntries(AMMO_TYPES.map(t => [t, Math.ceil((rounds[t] ?? 0) * d.share)]));
  const crates = cratesOf(scaled), key = AMMO_TYPES.map(t => crates[t] ?? 0).join(',');
  if (key === d.shown) return false;
  d.shown = key;
  if (!d.plan || AMMO_TYPES.some(t => (crates[t] ?? 0) > (d.plan!.full[t] ?? 0))) {
    const full = { ...crates }, planned = stackCrates(full, d.room), lay = layDump(planned, d.perRow, d.room);
    d.plan = { full, stacks: planned, at: lay.at };
    d.extent = { length: lay.length, width: lay.width };
  }
  const stacks = shownStacks(d.plan.stacks, crates, d.room.deck), lay = { at: d.plan.at };
  d.stacks = stacks;
  const byType = new Map<AmmoType, number>(), bySize = new Map<1 | 2, number>();
  for (const t of AMMO_TYPES) d.crates[t] = room(d, d.crates[t], crates[t] ?? 0, () => buildAmmoCrate(t, d.p).geometry, `ammo crates:${t}`);
  for (const k of [1, 2] as const) d.pallets[k] = room(d, d.pallets[k], d.room.deck > 0 ? stacks.filter(s => s.spots === k).length : 0, () => palletGeometry(k, d.p), `ammo pallets:${k}`);
  stacks.forEach((s, i) => {
    if (!s.crates.length) return;   // (an empty stack's pallet goes with its last crate)
    const at = lay.at[i];
    const pm = d.pallets[s.spots];
    if (pm) { const pi = bySize.get(s.spots) ?? 0; pm.setMatrixAt(pi, _m.makeTranslation(at.x, 0, at.z)); bySize.set(s.spots, pi + 1); }
    for (const c of s.crates) {
      const k = byType.get(c.type) ?? 0;
      d.crates[c.type]!.setMatrixAt(k, _m.makeTranslation(at.x + c.x, c.y, at.z + c.z)); byType.set(c.type, k + 1);
    }
  });
  const done = (im: THREE.InstancedMesh | undefined, n: number) => {
    if (!im) return;
    im.count = n; im.visible = n > 0; im.instanceMatrix.needsUpdate = true;
    if (n) im.computeBoundingSphere();
  };
  for (const t of AMMO_TYPES) done(d.crates[t], byType.get(t) ?? 0);
  for (const k of [1, 2] as const) done(d.pallets[k], bySize.get(k) ?? 0);
  return true;
}
/**
 * 69.31: the top of the highest crate of `type` the dump shows (the middle of its lid, in the dump's frame), where its arm takes
 * the next one from; null when it shows none.
 */
export function topCrateAt(d: AmmoDump, type: AmmoType): { x: number; y: number; z: number } | null {
  let best: { x: number; y: number; z: number } | null = null;
  d.stacks?.forEach((s, i) => {
    const at = d.plan!.at[i];
    for (const c of s.crates) if (c.type === type && (!best || c.y + AMMO_CRATES[type].h > best.y)) best = { x: at.x + c.x, y: c.y + AMMO_CRATES[type].h, z: at.z + c.z };
  });
  return best;
}
/** 69.34: a dump's cost as drawn — its draws (each type's crates, each pallet size) and triangles. */
export function ammoDumpCost(d: AmmoDump): { draws: number; triangles: number } {
  let draws = 0, triangles = 0;
  for (const im of [...Object.values(d.crates), ...Object.values(d.pallets)]) {
    if (!im || !im.visible || !im.count) continue;
    const g = im.geometry;
    draws++; triangles += ((g.index ? g.index.count : g.getAttribute('position').count) / 3) * im.count;
  }
  return { draws, triangles };
}

/**
 * 69.34: the supply truck's bed rounds (`StructureState.ammo`, with GameConfig.munitionsSupply) drawn at its tailgate, in the
 * bare row's place while it holds any (`buildAmmoBed`); none, the truck as before. Made the first time it holds rounds (the
 * truck's palette, asked for then); a key compare a frame after.
 */
export function setTruckAmmo(m: VehicleModel, ammo: Partial<Record<AmmoType, number>> | undefined, p: () => UnitPalette): void {
  const c = m.cargo;
  if (!c || (!ammo && !c.ammo)) return;
  if (!c.ammo) {
    const T = TECHNICAL_MODEL, cl = SUPPLY_TRUCK_MODEL.crate[2], bare = T.cabBack - 0.12 - cl / 2 - (SUPPLY_TRUCK_MODEL.rows - 1) * (cl + 0.04);
    c.ammo = buildAmmoBed(p(), MUNITIONS.truck);
    c.ammo.group.position.set(-BED_ROOM.length / 2, c.floor, bare - BED_ROOM.width / 2);   // (across the bed, its rows from the tailgate's side)
    c.rows[0].parent!.add(c.ammo.group);
  }
  if (!showAmmoDump(c.ammo, ammo)) return;
  const any = /[1-9]/.test(c.ammo.shown), last = c.rows[c.rows.length - 1];
  c.ammo.group.visible = any;
  const shown = c.k === 1 ? c.rows.length : Math.round(Math.max(0, c.k) * c.rows.length);
  last.visible = !any && c.rows.length - 1 < shown;
}
